import { useEffect, useMemo, useState } from "react";
import {
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
} from "reactflow";
import ClickableBaseEdge from "./clickableBaseEdge";
import { alpha, useTheme } from "@mui/material";
import { convertBitsToBytes } from "../../../utils/easyMaps/convertToBytes";
import { useOutletContext } from "react-router-dom";

export const getSpecialPath = (
  { sourceX, sourceY, targetX, targetY },
  offset,
) => {
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  return `M ${sourceX} ${sourceY} Q ${centerX} ${
    centerY + offset
  } ${targetX} ${targetY}`;
};

export default function PositionableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  data,
  selected,
}) {
  const theme = useTheme();
  const { configLayer } = useOutletContext();
  const { getEdges, setEdges, screenToFlowPosition } = useReactFlow();
  const {
    mapId,
    editMode,
    entangled,
    lineType,
    mapRef,
    itensData = null,
    animation = "desativada",
    showLabelEnlace,
    easyMapsConfig,
    variant = "trafego",
    host = "",
    corTraffic,
    corPower,
    isDown,
  } = data;

  const edges = getEdges();
  const positionHandlers = data?.positionHandlers ?? [];
  const edgeSegmentsCount = positionHandlers.length + 1;
  const colors = useMemo(() => {
    if (variant === "trafego") {
      return corTraffic?.cor || "gray";
    } else if (variant === "potencia") {
      return corPower?.cor || "gray";
    }
  }, [data, id, variant]);
  const convertOutputTraffic = useMemo(
    () => convertBitsToBytes(data?.tx_traffic),
    [data],
  );
  const convertInputTraffic = useMemo(
    () => convertBitsToBytes(data?.rx_traffic),
    [data],
  );
  const inputPower = useMemo(
    () => (isNaN(data?.rx_power) ? "-" : data?.rx_power),
    [data],
  );
  const outputPower = useMemo(
    () => (isNaN(data?.tx_power) ? "-" : data?.tx_power),
    [data],
  );

  let pathFunction;

  switch (lineType) {
    case "reta":
      pathFunction = getStraightPath;
      break;
    case "ortogonal":
      pathFunction = getSmoothStepPath;
      break;
    case "curvada":
      pathFunction = getBezierPath;
      break;
    default:
      pathFunction = getStraightPath;
  }

  const edgeSegmentsArray = Array.from({ length: edgeSegmentsCount }).map(
    (_, i) => {
      const segmentSource =
        i === 0 ? { x: sourceX, y: sourceY } : positionHandlers[i - 1];
      const segmentTarget =
        i === edgeSegmentsCount - 1
          ? { x: targetX, y: targetY }
          : positionHandlers[i];

      const { x: segmentSourceX, y: segmentSourceY } = segmentSource;
      const { x: segmentTargetX, y: segmentTargetY } = segmentTarget;

      const difX = targetX - sourceX;
      let _sourcePosition =
        i === 0 ? sourcePosition : difX < 0 ? "left" : "right";
      let _targetPosition =
        i === edgeSegmentsCount - 1
          ? targetPosition
          : difX < 0
            ? "right"
            : "left";

      const [edgePath, labelX, labelY] = pathFunction({
        sourceX: segmentSourceX,
        sourceY: segmentSourceY,
        sourcePosition: _sourcePosition,
        //sourcePosition,
        targetX: segmentTargetX,
        targetY: segmentTargetY,
        targetPosition: _targetPosition,
        //targetPosition,
      });

      return { edgePath, labelX, labelY };
    },
  );

  const styles = {
    clickableBaseEdge: {
      stroke: selected
        ? theme.palette.color.zabbixSeverty.selectedTranslucid
        : editMode
          ? "#5e5e5e"
          : colors,
      opacity: entangled ? 1 : 0.33,
      strokeWidth: easyMapsConfig?.configuracoes?.larguraEnlace - 3 || 3, // o default é 7, mas fica muiot grosso, então subtraio 3 para ficar mais próximo do que era antes
      animation:
        easyMapsConfig?.configuracoes?.animacao?.linha != false ? null : "none",
      strokeDasharray:
        editMode || animation === "desativada" || isDown
          ? "0"
          : animation === "pontilhada"
            ? "3,8"
            : "5",
    },
    divEdge: {
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      fontSize: 12,
      pointerEvents: "all",
    },
    buttonDraggable: {
      width: "20px",
      height: "20px",
      fontSize: "14px",
      borderRadius: "50%",
      cursor: "pointer",
      lineHeight: 4,
      padding: 0,
      background: theme.palette.color.zabbixSeverty.selectedTranslucid,
    },
    textLabel: {
      padding: "0px",
      m: "0px",
      width: "100%",

      display: "flex",
      justifyContent: "space-between",
    },
    boxContainerData: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    boxInfo: {
      display: "flex",
      flexDirection: "row",
      gap: "3px",
    },
    colorTextLayer: {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },

    label: {
      position: "absolute",
      // transform: `translate(-50%, -50%) translate(${sourceX + targetX / 6}px,${
      //   (sourceY + targetY) / 2
      // }px)`,
      background: alpha(theme.palette.background.paperLinkedMaps, 0.8),
      padding: "5px 10px",
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      justifyContent: "center",
      minWidth: "150px",
      width: "170px",
      borderRadius: 5,
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.color.textEasyMaps,
      opacity: entangled ? 1 : 0.33,
      zIndex: 1000,
    },
    edit: {
      position: "absolute",
      transform: `translate(-50%, -50%) translate(${
        (sourceX + targetX) / 2
      }px,${(sourceY + targetY) / 2}px)`,
      background: theme.palette.color.zabbixSeverty.selectedTranslucid,
      paddingLeft: 1,
      paddingRight: 1,
      borderRadius: 5,
      fontSize: 10,
      fontWeight: 500,
      color: "#FFFFFF",
      opacity: entangled ? 1 : 0.33,
    },
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
    @keyframes edgePulse {
      0% { stroke: ${theme.palette.enlaces.offline.main}; }
      50% { stroke: ${theme.palette.enlaces.p90.main}; }
      100% { stroke: ${theme.palette.enlaces.offline.main}; }
    }
    .edge-pulse {
      animation: edgePulse 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
    }
  `;
    document.head.appendChild(style);
    return () => style.remove();
  }, [theme]);

  const handleMouseMove = (event, id, handlerIndex) => {
    let activeEdge = parseInt(event.target.dataset.active ?? -1);
    if (activeEdge === -1) {
      return;
    }
    if (activeEdge === 1) {
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const edgeIndex = edges.findIndex((edge) => edge.id === id);
      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);
      setEdges((edges) => {
        //edges[edgeIndex].id = Math.random();
        edges[edgeIndex].data.positionHandlers[handlerIndex] = mapRef
          ? {
              x: position.x,
              y: position.y,
              active: 1,
              location: {
                long: latlng.lng,
                lat: latlng.lat,
              },
            }
          : {
              x: position.x,
              y: position.y,
              active: 1,
            };
        return edges;
      });
    }
  };
  const handleMouseUp = () => {
    if (editMode) {
      setEdges((edges) => {
        const updatedEdges = edges.map((edge) => ({
          ...edge,
          data: {
            ...edge.data,
            positionHandlers: edge.data.positionHandlers?.map((handler) => ({
              ...handler,
              active: -1,
            })),
          },
        }));

        return updatedEdges;
      });
    }
    return;
  };

  const handleMouseDown = (event, id, handlerIndex, active) => {
    if (editMode) {
      setEdges((edges) => {
        const edgeIndex = edges.findIndex((edge) => edge.id === id);
        edges[edgeIndex].data.positionHandlers[handlerIndex].active = 1;
        return edges;
      });
      event.preventDefault();
    }
    event.preventDefault();
    return;
  };

  const handleDeleteEdge = (event, id, handlerIndex) => {
    event.preventDefault();
    if (editMode) {
      //setEdges((edges) => edges.filter((edge) => edge.id !== id));
      setEdges((edges) => {
        const edgeIndex = edges.findIndex((edge) => edge.id === id);
        edges[edgeIndex].data.positionHandlers?.splice(handlerIndex, 1);
        return edges;
      });
    }
    return;
  };
  const transform = (handlerIndex, x, y) => {
    return handlerIndex === 0
      ? `translate(-50%, -50%) translate(${(x + sourceX) / 2}px,${
          (y + sourceY) / 2
        }px)`
      : handlerIndex === positionHandlers.length - 1
        ? `translate(-50%, -50%) translate(${(x + targetX) / 2}px,${
            (y + targetY) / 2
          }px)`
        : null;
  };
  const handleCreateEdge = (event, id, index) => {
    if (selected && editMode) {
      //event.preventDefault();
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let latlng = mapRef?.containerPointToLatLng([position.x, position.y]);

      setEdges((edges) => {
        const edgeIndex = edges.findIndex((edge) => edge.id === id);
        edges[edgeIndex].data.positionHandlers =
          edges[edgeIndex].data.positionHandlers ?? [];
        edges[edgeIndex].data.positionHandlers.splice(
          index,
          0,
          mapRef
            ? {
                x: position.x,
                y: position.y,
                active: -1,
                location: {
                  long: latlng.lng,
                  lat: latlng.lat,
                },
              }
            : {
                x: position.x,
                y: position.y,
                active: -1,
              },
        );
        return edges;
      });
    }
  };

  let positionLabel = 0;

  if (Array.isArray(positionHandlers) && Array.isArray(edgeSegmentsArray)) {
    const posLegnth = positionHandlers.length;
    const edsLegnth = edgeSegmentsArray.length;

    positionLabel =
      posLegnth % 2 !== 0
        ? positionHandlers[(posLegnth - 1) / 2]
        : {
            x: edgeSegmentsArray[(edsLegnth - 1) / 2].labelX,
            y: edgeSegmentsArray[(edsLegnth - 1) / 2].labelY,
          };
  } else {
    return;
  }

  const renderLabel = (
    positionLabel,
    itensData,
    convertInputTraffic,
    convertOutputTraffic,
  ) => (
    <div
      className="nodrag nopan"
      style={{
        ...styles.label,
        zIndex: 1,
        transform: `translate(-50%, -50%) translate(${positionLabel?.x}px,${positionLabel?.y}px)`,
      }}
    >
      <span
        style={{
          ...styles.textLabel,
          ...{ textAlign: "center" },
        }}
      >
        {host}
      </span>
      <span
        style={{
          ...styles.textLabel,
          ...styles.colorTextLayer,
          ...{ minHeight: "25px", justifyContent: "center" },
        }}
      >
        {"[" + (data?.interface || "--") + "]"}
      </span>

      {configLayer?.[mapId] == "trafego" && (
        <>
          <div style={styles.boxContainerData}>
            <span style={{ ...styles.colorTextLayer }}>{"TX:"}</span>
            <div style={styles.boxInfo}>
              <span>{convertOutputTraffic.valor || "-"}</span>
              <span style={{ ...styles.colorTextLayer }}>
                {convertOutputTraffic.unidade}
              </span>
            </div>
          </div>
          <div style={styles.boxContainerData}>
            <span style={{ ...styles.colorTextLayer }}>{"RX:"}</span>
            <div style={styles.boxInfo}>
              <span>{convertInputTraffic.valor || "-"}</span>
              <span style={{ ...styles.colorTextLayer }}>
                {convertInputTraffic.unidade}
              </span>
            </div>
          </div>
        </>
      )}
      {configLayer?.[mapId] == "potencia" && (
        <>
          <div style={styles.boxContainerData}>
            <span style={{ ...styles.colorTextLayer }}>{"TX:"}</span>
            <div style={styles.boxInfo}>
              <span>{inputPower}</span>
              <span style={{ ...styles.colorTextLayer }}>{"dBm"}</span>
            </div>
          </div>
          <div style={styles.boxContainerData}>
            <span style={{ ...styles.colorTextLayer }}>{"RX:"}</span>
            <div style={styles.boxInfo}>
              <span>{outputPower}</span>
              <span style={{ ...styles.colorTextLayer }}>{"dBm"}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {edgeSegmentsArray.map(({ edgePath, labelX, labelY }, index) => (
        <ClickableBaseEdge
          onDoubleClick={(event) => handleCreateEdge(event, id, index)}
          key={`edge${id}_segment${index}`}
          path={edgePath}
          markerEnd={
            index === edgeSegmentsArray.length - 1 ? markerEnd : "false"
          }
          className={isDown && !editMode ? "edge-pulse" : ""}
          style={styles.clickableBaseEdge}
        />
      ))}

      {positionHandlers.length === 0 && (
        <EdgeLabelRenderer key={`edge`}>
          {!editMode &&
            showLabelEnlace &&
            renderLabel(
              positionLabel,
              itensData,
              convertInputTraffic,
              convertOutputTraffic,
            )}
        </EdgeLabelRenderer>
      )}

      {positionHandlers.map(({ x, y, active }, handlerIndex) => (
        <EdgeLabelRenderer key={`edge${id}_handler${handlerIndex}`}>
          {!editMode &&
            showLabelEnlace &&
            renderLabel(
              positionLabel,
              itensData,
              convertInputTraffic,
              convertOutputTraffic,
            )}

          {editMode && selected && (
            <div
              data-active={active ?? -1}
              style={{
                ...styles.divEdge,
                width: active === 1 ? "1200px" : "20px",
                height: active === 1 ? "1200px" : "20px",
                transform: `translate(-50%, -50%) translate(${x}px,${y}px)`,
              }}
              className="nodrag nopan"
              onMouseMove={(event) => handleMouseMove(event, id, handlerIndex)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <button
                data-active={active ?? -1}
                style={{
                  ...styles.buttonDraggable,
                  border:
                    active === 1 ? "1px solid #FFFFFF" : "1px solid #33333300",
                }}
                className="edgebutton"
                onMouseDown={(event) =>
                  handleMouseDown(event, id, handlerIndex, active)
                }
                onContextMenu={(event) =>
                  handleDeleteEdge(event, id, handlerIndex)
                }
              ></button>
            </div>
          )}
        </EdgeLabelRenderer>
      ))}
    </>
  );
}
